import { assoc, complement, isEmpty, last, prop, propEq } from 'ramda'
import { capitalize } from '@btime/helpers'
import { useChangeFilterValue, useFilterValue } from '@btime/react-contexts'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { compactString } from '../../../utils/helpers/string'
import { usePlaceTypes } from '../../../utils/helpers/placeTypes'
import FilterLink from '../../../components/filter/FilterLink'
import LevelsLoader, { useLevels } from './_load/LevelsLoader'
import ModalPickerWrapper from '../../../components/form/modalPicker/ModalPickerWrapper'
import Panel from '../../../packages/kiti/components/structure/Panel'
import PlaceLevelPicker from '../../placeLevels/picker/PlaceLevelPicker'
import PlacePicker from '../picker/PlacePicker'
import Text from '../../../packages/kiti/components/presentation/Text'

const FilterItem = ({
  field,
  isPlace,
  modalLevel,
  level,
  value,
  valueFromOtherTypes,
  placeLevelsOnly,
  allPlaceLevelIds
}) => {
  const { t } = useTranslation('places')
  const handleChange = useChangeFilterValue()

  if (placeLevelsOnly && isPlace) return false

  let valueFromOtherLevels = []
  if (!isPlace) {
    valueFromOtherLevels = value?.filter(complement(propEq('levelId', level.id)))
    value = value?.filter(propEq('levelId', level.id))
  }

  let filter = { types: level.type }
  if (!isEmpty(allPlaceLevelIds)) filter = assoc('placeLevelIds', allPlaceLevelIds, filter)
  if (!isPlace) {
    const parentIds = valueFromOtherLevels.filter(placeLevel => placeLevel.levelId < level.id).map(prop('id'))
    filter = { levelIds: level.id }
    if (!isEmpty(parentIds)) filter = assoc('parentPathIds', parentIds, filter)
  }

  const commonProps = {
    type: 'listItem',
    placeholder: level.name,
    label: level.name,
    level: modalLevel + 1,
    value: value,
    onChange: value => handleChange(field, [...value, ...valueFromOtherTypes, ...valueFromOtherLevels]),
    multiple: true,
    filter,
    title: t('filters.selectLevel', { level: level.name })
  }

  if (isPlace) return <PlacePicker {...commonProps} />
  return <PlaceLevelPicker {...commonProps} />
}






const PlaceTypeFilters = ({ field, placeLevelField, type, level: modalLevel, placeLevelsOnly }) => {
  const levels = useLevels()
  const filteredLevels = levels.filter(propEq('type', type.key))
  const lastId = last(filteredLevels)?.id

  const formatPermissionKey = type => {
    return type.key
      .split('_')
      .map(text => capitalize(text))
      .join('')
  }

  const permissionKey = type.key.indexOf('_') >= 0 ? formatPermissionKey(type) : capitalize(type.key)

  const canSee = useSessionPermission(`list${permissionKey}`) && filteredLevels.length > 0

  const placeValue = useFilterValue(field, []).filter(propEq('type', type.key))
  const placeValueFromOtherTypes = useFilterValue(field, []).filter(complement(propEq('type', type.key)))
  const placeLevelValue = useFilterValue(placeLevelField, []).filter(propEq('type', type.key))
  const placeLevelValueFromOtherTypes = useFilterValue(placeLevelField, []).filter(complement(propEq('type', type.key)))

  if (!canSee) return false

  return (
    <>
      <Panel padding={0.2} bgColor="light2" radius={0}>
        <Text key={type.id} align="center">
          {type.name}
        </Text>
      </Panel>

      {filteredLevels.map(level => {
        const isPlace = level.id === lastId
        return (
          <FilterItem
            key={level.id}
            isPlace={isPlace}
            modalLevel={modalLevel}
            value={isPlace ? placeValue : placeLevelValue}
            field={isPlace ? field : placeLevelField}
            allPlaceLevelIds={placeLevelValue.map(prop('id'))}
            valueFromOtherTypes={isPlace ? placeValueFromOtherTypes : placeLevelValueFromOtherTypes}
            level={level}
            placeLevelsOnly={placeLevelsOnly}
          />
        )
      })}
    </>
  )
}

const FullPlacesFilter = ({ field, placeLevelField, label, icon, level, placeLevelsOnly, types, ...props }) => {
  const { t } = useTranslation(['places', 'common'])
  const [open, setOpen] = React.useState(false)

  field = field || 'placeIds'
  placeLevelField = placeLevelField || 'placeLevelIds'

  const placeValue = useFilterValue(field, [])
  const placeLevelValue = useFilterValue(placeLevelField, [])
  const [placeTypes] = usePlaceTypes()

  const totalSelecionados = (placeValue?.length || 0) + (placeLevelValue?.length || 0);

  const filterText = totalSelecionados === 0 
    ? t('common:all', 'Todos') 
    : t('common:selectedItems', { count: totalSelecionados, defaultValue: '{{count}} selecionado(s)' });
 return (
    <div>
      <FilterLink
        icon={icon || 'RiMapPinLine'}
        label={label || t('filters.destination')}
        value={filterText}
        onClick={() => setOpen(true)}
      />

      <ModalPickerWrapper
        title={t('filters.selectDestinations')}
        open={open}
        onClose={() => setOpen(false)}
        level={level}
      >
        <LevelsLoader filter={{ types }}>
          {placeTypes.map(type => (
            <PlaceTypeFilters
              type={type}
              key={type.id}
              level={level || 1}
              placeValue={placeValue}
              placeLevelValue={placeLevelValue}
              field={field}
              placeLevelField={placeLevelField}
              placeLevelsOnly={placeLevelsOnly}
            />
          ))}
        </LevelsLoader>
      </ModalPickerWrapper>
    </div>
  )
}

export default FullPlacesFilter

